import React, { useState, useEffect } from 'react';
import { normalizeVietnamese } from '../../utils/normalizeVietnamese';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { Task, TaskStatus, TaskPriority } from 'public-contracts';
import { useTranslation } from '../../hooks/useTranslation';
import { User } from '../../services/usersService';
import { Check, ChevronsUpDown, User as UserIcon, X, Plus, Upload, FileIcon, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useFileUpload } from '../../hooks/useFileUpload';
import { assistantService, TaskEnhancementRequest } from '../../services/assistantService';
import { useNotification } from '../../hooks/useNotification';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: Task | null;
  users: User[];
  initialStatus?: TaskStatus;
  onSave: (taskData: Partial<Task>) => Promise<void>;
  onDelete?: () => Promise<void>;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onOpenChange,
  task,
  users,
  initialStatus,
  onSave,
  onDelete,
}) => {
  const { translate, locale } = useTranslation();
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const { uploadFile, getFileUrl } = useFileUpload();
  const [tagInput, setTagInput] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<Task>>({
    title: '',
    description: '',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    assigneeId: '',
    dueDate: undefined,
    estimatedHours: 0,
    tags: [],
    attachments: [],
  });

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        assigneeId: task.assigneeId || '',
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        estimatedHours: task.estimatedHours || 0,
        tags: task.tags || [],
        attachments: task.attachments || [],
      });
    } else {
      setFormData({
        title: '',
        description: '',
        status: initialStatus || TaskStatus.TODO,
        priority: TaskPriority.MEDIUM,
        assigneeId: '',
        dueDate: undefined,

        estimatedHours: 0,
        tags: [],
        attachments: [],
      });
    }
    setTagInput('');
  }, [task, initialStatus, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setDeleteDialogOpen(false);
    setLoading(true);
    try {
      await onDelete();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const mediaId = await uploadFile(file);
      if (mediaId) {
        setFormData((prev) => ({
          ...prev,
          attachments: [...(prev.attachments || []), mediaId],
        }));
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
            const file = items[i].getAsFile();
            if (file) {
                setUploading(true);
                try {
                  const mediaId = await uploadFile(file);
                  if (mediaId) {
                    setFormData((prev) => ({
                      ...prev,
                      attachments: [...(prev.attachments || []), mediaId],
                    }));
                  }
                } catch (error) {
                  console.error('Failed to upload pasted image:', error);
                } finally {
                  setUploading(false);
                }
            }
        }
    }
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((id) => id !== attachmentId),
    }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value) : undefined;
    setFormData((prev) => ({ ...prev, dueDate: date }));
  };

  const handeAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: (prev.tags || []).filter((tag) => tag !== tagToRemove),
    }));
  };

  const selectedUser = users.find((u) => u.id === formData.assigneeId);

  const getInitials = (user: User) => {
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.username?.[0]?.toUpperCase() || '?';
  };

  const handleAiEnhance = async () => {
    // Check if there's substantial existing data (more than just title)
    // If we have description, tags, dates, hours, or assignee, treat it as existing data to refine
    // Otherwise, use title to autofill all fields
    const hasExistingData =
      (formData.description && formData.description.trim().length > 10) ||
      (formData.tags && formData.tags.length > 0) ||
      formData.dueDate ||
      (formData.estimatedHours && formData.estimatedHours > 0) ||
      formData.assigneeId ||
      task; // If editing an existing task, always refine

    // Determine the mode: if we have existing data, refine it; otherwise, use title to autofill
    const userPrompt = hasExistingData
      ? 'Refine and improve all existing fields while preserving the intent and context. Enhance descriptions, adjust priorities if needed, suggest better tags, and improve overall task quality.'
      : undefined; // No prompt means autofill from title

    setAiLoading(true);
    try {
      const request: TaskEnhancementRequest = {
        currentTask: {
          title: formData.title || '',
          description: formData.description || '',
          status: formData.status,
          priority: formData.priority,
          assigneeId: formData.assigneeId || '',
          dueDate: formData.dueDate ? formData.dueDate.toISOString().split('T')[0] : '',
          estimatedHours: formData.estimatedHours || 0,
          tags: formData.tags || [],
        },
        userPrompt,
        locale: locale || 'en',
      };

      const enhanced = await assistantService.enhanceTask(request);

      // Update form data with enhanced fields
      const updates: Partial<Task> = {};
      if (enhanced.title) updates.title = enhanced.title;
      if (enhanced.description !== undefined) updates.description = enhanced.description;
      if (enhanced.status) updates.status = enhanced.status as TaskStatus;
      if (enhanced.priority) updates.priority = enhanced.priority as TaskPriority;
      if (enhanced.dueDate) updates.dueDate = new Date(enhanced.dueDate);
      if (enhanced.estimatedHours !== undefined) updates.estimatedHours = enhanced.estimatedHours;
      if (enhanced.tags && enhanced.tags.length > 0) {
        // Merge with existing tags, avoiding duplicates
        const existingTags = formData.tags || [];
        const newTags = [...new Set([...existingTags, ...enhanced.tags])];
        updates.tags = newTags;
      }

      setFormData((prev) => ({ ...prev, ...updates }));

      notify(
        translate(
          hasExistingData
            ? 'pos.project_management.ai_refine_success'
            : 'pos.project_management.ai_autofill_success',
          hasExistingData
            ? 'Task fields have been refined with AI'
            : 'Task fields have been enhanced with AI'
        ),
        { type: 'success' }
      );

      // Show suggestions if available
      if (enhanced.suggestions && enhanced.suggestions.length > 0) {
        console.log('AI suggestions:', enhanced.suggestions);
      }
    } catch (error: any) {
      console.error('Failed to enhance task with AI:', error);
      notify(
        translate(
          'pos.project_management.ai_autofill_error',
          'Failed to enhance task fields. Please try again.'
        ),
        { type: 'error' }
      );
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" onPaste={handlePaste}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {task
                ? translate('pos.project_management.edit_task', 'Edit Task')
                : translate('pos.project_management.create_task', 'Create Task')}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="title">
                  {translate('pos.project_management.fields.title', 'Title')}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleAiEnhance}
                        disabled={aiLoading || !formData.title?.trim()}
                        className="h-7 px-2"
                      >
                        {aiLoading ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {translate(
                          'pos.project_management.ai_enhance_tooltip',
                          'AI Enhance: Autofill fields from title, or refine existing data'
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">
                {translate(
                  'pos.project_management.fields.description',
                  'Description'
                )}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="status">
                  {translate('pos.project_management.fields.status', 'Status')}
                </Label>
                <Select
                  value={formData.status}
                  onValueChange={(val) =>
                    setFormData((prev) => ({ ...prev, status: val as TaskStatus }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(TaskStatus).map((status) => (
                      <SelectItem key={status} value={status}>
                        {translate(`pos.project_management.status.${status}`, status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="priority">
                  {translate('pos.project_management.fields.priority', 'Priority')}
                </Label>
                <Select
                  value={formData.priority}
                  onValueChange={(val) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: val as TaskPriority,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(TaskPriority).map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {translate(
                          `pos.project_management.priority.${priority}`,
                          priority
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="dueDate">
                  {translate('pos.project_management.fields.due_date', 'Due Date')}
                </Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={
                    formData.dueDate
                      ? formData.dueDate.toISOString().split('T')[0]
                      : ''
                  }
                  onChange={handleDateChange}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="estimatedHours">
                  {translate(
                    'pos.project_management.fields.estimated_hours',
                    'Estimated Hours'
                  )}
                </Label>
                <Input
                  id="estimatedHours"
                  type="number"
                  value={formData.estimatedHours}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      estimatedHours: parseFloat(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="assigneeId">
                {translate('pos.project_management.fields.assignee', 'Assignee')}
              </Label>
              <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={assigneeOpen}
                    className="justify-between w-full font-normal"
                  >
                    {selectedUser ? (
                      <div className="flex items-center gap-2">
                        {selectedUser.avatar ? (
                          <img
                            src={selectedUser.avatar}
                            alt=""
                            className="h-5 w-5 rounded-full"
                          />
                        ) : (
                          <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                            {getInitials(selectedUser)}
                          </div>
                        )}
                        <span>
                          {selectedUser.firstName} {selectedUser.lastName}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">
                        {translate(
                          'pos.project_management.select_assignee',
                          'Select assignee...'
                        )}
                      </span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command
                    filter={(value, search) => {
                      const normalizedValue = normalizeVietnamese(value);
                      const normalizedSearch = normalizeVietnamese(search);
                      if (normalizedValue.includes(normalizedSearch)) return 1;
                      return 0;
                    }}
                  >
                    <CommandInput
                      placeholder={translate('common.search', 'Search...')}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {translate('common.no_results', 'No results found.')}
                      </CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="unassigned"
                          onSelect={() => {
                            setFormData((prev) => ({ ...prev, assigneeId: '' }));
                            setAssigneeOpen(false);
                          }}
                        >
                          <div className="flex items-center gap-3 py-1">
                            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                              <UserIcon className="h-4 w-4" />
                            </div>
                            <span className="font-medium text-sm">
                              {translate('common.unassigned', 'Unassigned')}
                            </span>
                          </div>
                          <Check
                            className={cn(
                              'ml-auto h-4 w-4',
                              !formData.assigneeId ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                        </CommandItem>
                        {users.map((user) => (
                          <CommandItem
                            key={user.id}
                            value={`${user.firstName || ''} ${user.lastName || ''} ${
                              user.username || ''
                            } ${user.email || ''} ${user.id}`}
                            onSelect={() => {
                              setFormData((prev) => ({
                                ...prev,
                                assigneeId: user.id === formData.assigneeId ? '' : user.id,
                              }));
                              setAssigneeOpen(false);
                            }}
                          >
                            <div className="flex items-center gap-3 py-1">
                              {user.avatar ? (
                                <img
                                  src={user.avatar}
                                  alt=""
                                  className="h-8 w-8 rounded-full"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                  {getInitials(user)}
                                </div>
                              )}
                              <div className="flex flex-col">
                                <span className="font-medium text-sm">
                                  {user.firstName} {user.lastName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {user.email}
                                </span>
                              </div>
                            </div>
                            <Check
                              className={cn(
                                'ml-auto h-4 w-4',
                                formData.assigneeId === user.id
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="tags">
                {translate('pos.project_management.fields.tags', 'Tags')}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handeAddTag();
                    }
                  }}
                  placeholder={translate(
                    'pos.project_management.add_tag_placeholder',
                    'Press Enter to add tag'
                  )}
                />
                <Button type="button" variant="outline" size="icon" onClick={handeAddTag}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {formData.tags?.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1 px-2 py-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:bg-muted rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid gap-2">
              <Label>
                {translate('pos.project_management.fields.attachments', 'Attachments')}
              </Label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {formData.attachments?.map((attachmentId) => (
                    <div
                      key={attachmentId}
                      className="flex items-center gap-2 bg-muted p-2 rounded-md border text-sm max-w-full"
                    >
                      <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate max-w-[150px] text-xs font-mono">
                        {attachmentId.substring(0, 8)}...
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(attachmentId)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={uploading}
                    asChild
                  >
                    <label className="cursor-pointer">
                      {uploading ? (
                        <span className="animate-pulse">Uploading...</span>
                      ) : (
                        <>
                          <Upload className="h-3 w-3 mr-2" />
                          {translate('pos.common.upload', 'Upload File')}
                        </>
                      )}
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploading}
                      />
                    </label>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4 flex justify-between sm:justify-between items-center">
            {task && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteClick}
                disabled={loading}
              >
                {translate('common.delete', 'Delete')}
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {translate('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? translate('common.saving', 'Saving...')
                  : translate('common.save', 'Save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate('pos.project_management.delete_confirm_title', 'Delete Task')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                'pos.project_management.delete_confirm_desc',
                'Are you sure you want to delete this task? This action cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>
              {translate('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={loading}
            >
              {translate('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
};
